import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockClosedIcon } from "@heroicons/react/outline";
import { getPeriodClosedError } from "../../../../Utils";

/**
 * PeriodClosedNotice — what a writer shows when the server rejected a save
 * because the period is closed.
 *
 * Reaching this is a data race: the pickers and disabled submits are meant to
 * prevent it, so by the time a 409 arrives the client's cached boundary is
 * provably stale. Hence `onRefresh` — the component that observes the rejection
 * is the right place to correct the cache, and doing it here means every writer
 * gets it without repeating the effect.
 *
 * Renders `null` for any other error, so callers keep their usual red FormError
 * for genuine failures and only swap tone for this one case. Amber, never red:
 * nothing broke, the books are simply closed. The backend's `message` is printed
 * verbatim — it already names the boundary date and the recovery step in
 * Indonesian, and rewriting it here would only risk contradicting the server.
 */
const PeriodClosedNotice = ({
  error,
  onRefresh,
  manageTo,
  // The embed funnel has no period route of its own, and every route it could
  // link to lives outside the iframe. It keeps the notice and drops the link.
  showManage = true,
  className = "",
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const periodError = getPeriodClosedError(error);
  const rejected = Boolean(periodError);
  const observedThrough = periodError?.closedThrough || null;

  useEffect(() => {
    if (!rejected || !onRefresh) return;
    onRefresh();
    // Scalars, not the object, so this fires once per rejection rather than on
    // every render. `observedThrough` is in there because a second rejection can
    // report a *later* boundary while this notice is still mounted — closing
    // again from another tab — and the cache has to follow the server, not stop
    // at the first boundary it heard. `onRefresh` is the hook's memoised refetch.
  }, [rejected, observedThrough, onRefresh]);

  if (!periodError) return null;

  const target =
    manageTo ||
    (location.pathname.startsWith("/v2/")
      ? "/v2/accounting/period"
      : "/period-close");

  return (
    <div
      className={`rounded-md border border-amber-200 bg-amber-50 p-4 ${className}`}
      role="status"
    >
      <div className="flex gap-3">
        <LockClosedIcon
          className="h-5 w-5 flex-shrink-0 text-amber-400"
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-sm text-amber-800">
            {periodError.message || t("accounting.period.lockedRow")}
          </p>
          {showManage && (
            <Link
              to={target}
              className="mt-1.5 inline-block text-sm font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
            >
              {t("accounting.period.banner.manage")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeriodClosedNotice;
